import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApi } from "~/api/provider";
import { useMembers, useSaveDocumentBody, useTaskize } from "~/api/queries";
import type { Document } from "~/api/types";
import { confirmTaskize } from "~/features/taskize/confirm";
import { buildRows } from "~/features/taskize/rows";
import { TaskizeTray } from "~/features/taskize/TaskizeTray";
import { useTaskizeTray } from "~/features/taskize/useTaskizeTray";

/**
 * The tray's owner: scene-level, so it unmounts with the document, but driven by a store so
 * the ✨ menu, the ⋯ menu and the shell-level Ask bar can all ask for it (§C1.2).
 *
 * It runs the extraction, turns it into rows against the document's *current* markdown (which
 * is what makes dedupe work), and commits through `confirmTaskize`.
 */
export function TaskizeHost({ document, canUpdate }: { document: Document; canUpdate: boolean }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const open = useTaskizeTray((state) => state.open);
  const documentId = useTaskizeTray((state) => state.documentId);
  const selectedText = useTaskizeTray((state) => state.selectedText);
  const rows = useTaskizeTray((state) => state.rows);
  const thinkerName = useTaskizeTray((state) => state.thinkerName);
  const pending = useTaskizeTray((state) => state.pending);
  const takePending = useTaskizeTray((state) => state.takePending);
  const setRows = useTaskizeTray((state) => state.setRows);
  const patchRow = useTaskizeTray((state) => state.patchRow);
  const removeRows = useTaskizeTray((state) => state.removeRows);
  const closeTray = useTaskizeTray((state) => state.closeTray);

  const taskize = useTaskize();
  const saveBody = useSaveDocumentBody();
  const { data: members } = useMembers();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The extraction runs once per open, not once per render of a store subscriber.
  const ranFor = useRef<string | null>(null);

  // The shell asked; this scene is the one that can answer.
  useEffect(() => {
    if (pending && pending.documentId === document.id) {
      takePending();
    }
  }, [pending, document.id, takePending]);

  useEffect(() => {
    if (!open || documentId !== document.id) {
      ranFor.current = null;
      return;
    }
    const token = `${documentId}:${selectedText ?? ""}`;
    if (ranFor.current === token) {
      return;
    }
    ranFor.current = token;
    setError(null);
    taskize.mutate(
      {
        documentId: document.id,
        ...(document.collectionId ? { collectionId: document.collectionId } : {}),
        documentTitle: document.title,
        ...(selectedText ? { selectedText } : {}),
      },
      {
        onSuccess: (result) => {
          setRows(buildRows(result, document.text), result.thinkerName);
        },
        onError: (failure) => {
          setError(
            failure instanceof Error && failure.message
              ? failure.message
              : t("Taskize failed — the thinker returned no result."),
          );
        },
      },
    );
  }, [open, documentId, selectedText, document, setRows, taskize, t]);

  if (!open || documentId !== document.id) {
    return null;
  }

  const onConfirm = async (keys: string[]) => {
    const wanted = new Set(keys);
    const chosen = {
      tasks: rows.tasks.filter((row) => wanted.has(row.key)),
      decisions: rows.decisions.filter((row) => wanted.has(row.key)),
      reminders: rows.reminders.filter((row) => wanted.has(row.key)),
    };
    setBusy(true);
    try {
      // Re-read immediately before the splice: the window for a lost update is one tick, and
      // sculpt's checkpoint is what makes the remainder recoverable (§E2 R5).
      const fresh = await api.documents.get(document.id);
      const outcome = await confirmTaskize(chosen, {
        api,
        documentId: document.id,
        markdown: fresh.text,
        lane: document.editorLane === "v2" ? "v2" : "v1",
        saveBody: (markdown) => saveBody.mutateAsync({ documentId: document.id, markdown }),
        locale: i18n.language,
      });
      if (outcome.tasksCreated > 0) {
        toast.success(t("{{count}} tasks added", { count: outcome.tasksCreated }));
      }
      if (outcome.decisionsWritten > 0) {
        toast.success(t("{{count}} decisions recorded", { count: outcome.decisionsWritten }));
      }
      if (outcome.remindersSet > 0) {
        toast.success(t("{{count}} reminders set", { count: outcome.remindersSet }));
      }
      removeRows(outcome.committedKeys);
    } catch {
      // The decision block failed, which aborts the whole confirm BEFORE any task is created —
      // so nothing partial has happened and the rows stay exactly as they were.
      toast.error(t("Unable to add Taskize items"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TaskizeTray
      rows={rows}
      loading={taskize.isPending}
      error={error}
      thinkerName={thinkerName}
      members={members ?? []}
      // Sculpt refuses the v2 lane and the propose fallback there can never be accepted, so a
      // decision written from here would be a proposal nobody could take (§C5, D3).
      decisionsDisabled={document.editorLane === "v2" || !canUpdate}
      busy={busy}
      onPatch={patchRow}
      onConfirm={(keys) => void onConfirm(keys)}
      onClose={closeTray}
    />
  );
}
